import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import AssetImage from "@/components/AssetImage";
import { useAsset } from "@/hooks/useAsset";
import { formatChallengeDateRange } from "@/utils/formatChallengeDateRange";
import { isColorDarkOrLight } from "@/utils/isColorDarkOrLight";
import { sanitizeCssUrl } from "@/utils/urlSanitization";
import { isMobile } from "react-device-detect";
import type { FormattedChallenge } from "@/types/play";
import "./ActiveMissionCard.scss";

interface ActiveMissionCardProps {
    challenges: FormattedChallenge[];
    isLoading: boolean;
}

function getCurrentEstTime() {
    return DateTime.now().setZone("America/New_York")
        .toMillis();
}

/**
 * ActiveMissionCard - Hero-sized brand challenge display for CockpitV3
 * This is the future standard for displaying active missions in DevourPlay.
 * Built specifically for the CockpitV3 dashboard with optimal layout for the 68% width hero section.
 * Uses challenge data but with its own presentation optimized for prominent brand placement.
 */
const ActiveMissionCard: React.FC<ActiveMissionCardProps> = ({
    challenges,
    isLoading,
}) => {
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [currentDate, setCurrentDate] = useState(getCurrentEstTime());
    const [titleFontSize, setTitleFontSize] = useState(42);

    const displayChallenges = (challenges || []).slice(0, 3);
    const currentChallenge = displayChallenges[activeIndex];


    // Load background image - always use small for card display
    const { data: backgroundImage } = useAsset(currentChallenge?.backgroundImageSmall as any || null);

    // Update time periodically
    useEffect(() => {
        const interval = setInterval(() => setCurrentDate(getCurrentEstTime()), 10000);
        return () => clearInterval(interval);
    }, []);

    // Auto-rotate challenges
    useEffect(() => {
        if (displayChallenges.length <= 1) return;

        const interval = setInterval(() => {
            setActiveIndex((prev) => (prev + 1) % displayChallenges.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [displayChallenges.length]);

    // Calculate responsive font size based on title length
    useEffect(() => {
        if (!currentChallenge) return;

        const title = currentChallenge.cardTitle || currentChallenge.name || "";
        const titleLength = title.length;

        // Check if mobile viewport
        const isMobile = window.innerWidth < 768;

        // Adjust font size based on title length and viewport
        if (isMobile) {
            // Mobile font sizes (smaller)
            if (titleLength > 50) {
                setTitleFontSize(20);
            } else if (titleLength > 35) {
                setTitleFontSize(22);
            } else if (titleLength > 25) {
                setTitleFontSize(24);
            } else {
                setTitleFontSize(28);
            }
        } else {
            // Desktop font sizes
            if (titleLength > 50) {
                setTitleFontSize(28);
            } else if (titleLength > 35) {
                setTitleFontSize(32);
            } else if (titleLength > 25) {
                setTitleFontSize(36);
            } else {
                setTitleFontSize(42);
            }
        }
    }, [currentChallenge]);

    const handleChallengeClick = () => {
        if (!currentChallenge) return;
        navigate(currentChallenge.brandMapSlug
            ? `/brands/${currentChallenge.brandMapSlug}`
            : `/challenges/${currentChallenge.id}`);
    };

    const getChallengeLabel = (challenge: FormattedChallenge) => {
        if (challenge.end <= currentDate) {
            return "Debrief";
        }
        if (challenge.isAccepted) {
            return "Monitor";
        }
        return "Scout";
    };


    if (isLoading) {
        return (
            <div className="active-mission-card active-mission-card--loading">
                <div className="loading-shimmer">
                    <div className="loading-text">Loading Mission...</div>
                </div>
            </div>
        );
    }

    // Show discovery mode when no active challenge
    if (!currentChallenge) {
        return (
            <div className="active-mission-card active-mission-card--discover">
                <div className="discover-hero">
                    {/* PLAY.EARN.REPEAT Header Image */}
                    <div className="discover-header">
                        <img
                            src="/images/play-earn-repeat.svg"
                            alt="PLAY. EARN. REPEAT."
                            className="play-earn-repeat-logo"
                            onError={(e) => {
                                // Fallback to text if image fails
                                e.currentTarget.style.display = "none";
                                const fallback = e.currentTarget.parentElement?.querySelector(".fallback-text");
                                if (fallback) {
                                    (fallback as HTMLElement).style.display = "block";
                                }
                            }}
                        />
                        <div className="fallback-text" style={{ display: "none" }}>
                            <h1>PLAY. EARN. REPEAT.</h1>
                        </div>
                    </div>

                    {/* Compelling Copy */}
                    <div className="discover-content">
                        <p className="hero-description">
                            DevourPlay lets you rack up XP, crush quests, & unlock real rewards — just play!
                        </p>

                        {/* Rotating Game Carousel */}
                        <div className="discover-games">
                            <div className="games-carousel">
                                <div className="games-scroll">
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-1.png`} alt="Game 1" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-2.png`} alt="Game 2" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-3.png`} alt="Game 3" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-4.png`} alt="Game 4" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-5.png`} alt="Game 5" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-6.png`} alt="Game 6" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-7.png`} alt="Game 7" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-8.png`} alt="Game 8" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-9.png`} alt="Game 9" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-10.png`} alt="Game 10" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-11.png`} alt="Game 11" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-12.png`} alt="Game 12" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-13.png`} alt="Game 13" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-14.png`} alt="Game 14" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-15.png`} alt="Game 15" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-16.png`} alt="Game 16" className="game-icon" />
                                    {/* Full duplicate set for seamless loop */}
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-1.png`} alt="Game 1" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-2.png`} alt="Game 2" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-3.png`} alt="Game 3" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-4.png`} alt="Game 4" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-5.png`} alt="Game 5" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-6.png`} alt="Game 6" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-7.png`} alt="Game 7" className="game-icon" />
                                    <img src={`${import.meta.env.VITE_CDN_URL}/images/lp-devourplay-carousel-img-8.png`} alt="Game 8" className="game-icon" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="active-mission-card"
            style={{
                backgroundImage: backgroundImage?.cdnUrl
                    ? `url(${sanitizeCssUrl(backgroundImage.cdnUrl)})`
                    : undefined,
                borderColor: currentChallenge.brandColor
                    ? `${currentChallenge.brandColor}4D` // 30% opacity in hex (4D = 77 in decimal = 30% of 255)
                    : undefined,
            }}
        >
            {/* Gradient overlay for text readability */}
            <div className="mission-overlay" />

            {/* Top row - Logo and Date */}
            <div className="mission-top">
                {currentChallenge.brandLogo &&
          <AssetImage
              assetId={currentChallenge.brandLogo as any}
              alt="brand-logo"
              className="brand-logo"
          />
                }
                <div className="mission-date">
                    {currentChallenge.start && currentChallenge.end
                        ? formatChallengeDateRange(currentChallenge.start, currentChallenge.end)
                        : "Coming Soon"}
                </div>
            </div>

            {/* Title */}
            <h2 className="mission-title" style={{ fontSize: `${titleFontSize}px` }}>
                {currentChallenge.cardTitle || currentChallenge.name}
            </h2>

            {/* Description and Button row */}
            <div className="mission-footer">
                <p className="mission-description">
                    {currentChallenge.description}
                </p>

                <button
                    className={`mission-action-btn ${isColorDarkOrLight(currentChallenge.brandColor || "#7859E6")}`}
                    style={{ backgroundColor: currentChallenge.brandColor || "#7859E6" }}
                    onClick={handleChallengeClick}
                >
                    {getChallengeLabel(currentChallenge)}
                </button>
            </div>

            {/* Pagination dots if multiple challenges */}
            {displayChallenges.length > 1 &&
        <div className="mission-pagination">
            {displayChallenges.map((_, index) =>
                <button
                    key={index}
                    className={`dot ${index === activeIndex ? "dot--active" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        setActiveIndex(index);
                    }}
                    aria-label={`Go to challenge ${index + 1}`}
                />)}
        </div>
            }
        </div>
    );
};

export default ActiveMissionCard;